import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { FormSubmitButton, FormTextInput, FormTextAreaInput, NavigateLinks, FormSelectInput } from '@/components';
import { officeSchema, OfficeSchema } from '../../models';
import { editOfficeBreadcrumbs } from '../../config';
import { useOffice, useCitiesOptions } from '../../hooks';
import { useUpdateOfficeMutation } from '../../services/offices.service';
import { zodResolver } from '@hookform/resolvers/zod';

const EditOffice = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { office, isLoading } = useOffice({ officeId: id! });
  const { handleSubmit, control, setValue } = useForm<OfficeSchema>({
    resolver: zodResolver(officeSchema),
    defaultValues: office,
  });
  const { options } = useCitiesOptions();

  const breadcrumbs = useMemo(() => editOfficeBreadcrumbs(navigate), []);

  const [updateOffice, { isLoading: isUpdating }] = useUpdateOfficeMutation();

  useEffect(() => {
    if (office) {
      setValue('name', office.name || '');
      setValue('description', office.description || '');
      setValue('address', office.address || '');
      setValue('cityId', office.cityId || '');
    }
  }, [office, setValue]);

  const onSubmit = async (data: OfficeSchema) => {
    await updateOffice({ id: id!, body: data });
    navigate('/inventory/geography/offices');
  };

  if (isLoading) return <p>Cargando...</p>;

  return (
    <section className='flex flex-col w-full h-full gap-10'>
      <NavigateLinks actualPage='Editar oficina' breadcrumbs={breadcrumbs} />
      <form
        className='flex flex-col gap-5 bg-white p-12 rounded-lg shadow-md mx-auto max-w-[700px] w-full'
        onSubmit={handleSubmit(onSubmit)}
      >
        <h2 className='text-2xl font-bold select-none'>Editar oficina</h2>
        <div className='flex flex-col gap-4'>
          <FormSelectInput
            name='cityId'
            placeholder='Ciudad'
            control={control}
            options={options}
            required
          />
          <FormTextInput
            name='address'
            placeholder='Dirección'
            control={control}
            required
          />
          <FormTextInput
            name='name'
            placeholder='Nombre'
            control={control}
            autoFocus
            required
          />
          <FormTextAreaInput
            name='description'
            placeholder='Descripción'
            control={control}
            rows={2}
            required
          />
        </div>
        <FormSubmitButton
          isLoading={isUpdating}
          label='Guardar cambios'
          icon={' '}
          action={handleSubmit(onSubmit)}
        />
      </form>
    </section>
  );
};

export default EditOffice; 